import React, { useState, useEffect } from 'react';
import { Server, FileJson } from 'lucide-react';
import { CommonSongsSection } from './CommonSongsSection';
import { TodaySection } from './TodaySection';
import { JingleReportButton } from './JingleReportButton';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit', hour12: false }) : '—');

const REASONS = [
  [/^source_cooldown:icy/, 'η πηγή τίτλων δεν απαντά (σε αναμονή)'],
  [/^source_cooldown:/, 'η πηγή στοιχείων δεν απαντά (σε αναμονή)'],
  [/^icy_timeouts/, 'η πηγή τίτλων δεν απαντά επανειλημμένα'],
  [/^poll_errors/, 'ο έλεγχος του σταθμού αποτυγχάνει'],
  [/^station_cooldown/, 'ο σταθμός είναι σε αναμονή μετά από σφάλματα'],
  [/^metadata_errors/, 'σφάλματα στους τίτλους'],
  [/^shazam_errors/, 'η αναγνώριση Shazam αποτυγχάνει'],
];
export const plainReason = (r) => (REASONS.find(([re]) => re.test(r || '')) || [null, r || 'άγνωστο'])[1];

const PROBLEMS = {
  shazam_down: () => 'Το Shazam δεν αναγνωρίζει τίποτα σε αυτόν τον server',
  memory_high: (p) => `Η μνήμη είναι πάνω από 75% του ορίου (${p.rss_mb} από ${p.limit_mb} MB) — κίνδυνος επανεκκίνησης`,
  db_writes_slow: () => 'Η αποθήκευση στη βάση αργεί πάνω από 1 δευτ. ανά εγγραφή',
  server_slow: () => 'Ο server αργεί να απαντήσει (πάνω από 1 δευτ.)',
};

const ServerCard = ({ s, firstSaved }) => {
  const id = s.server.toLowerCase();
  const problems = s.lasting_problems || [];
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2.5 text-[11px] space-y-1.5 bg-white dark:bg-gray-900" data-testid={`fleet-server-card-${id}`}>
      <div className="flex items-center justify-between">
        <span className="font-semibold text-gray-900 dark:text-white">{s.server}</span>
        <span className={s.stale ? 'text-red-600' : (s.monitor_running ? 'text-green-600' : 'text-gray-500')} data-testid={`fleet-server-state-${id}`}>
          {s.stale ? `χωρίς σήμα από ${fmtTime(s.updated_at)}` : (s.monitor_running ? 'σε λειτουργία' : 'δεν καταγράφει')}
        </span>
      </div>
      <div className="text-gray-500">ξεκίνησε στις {fmtTime(s.boot_at)}</div>
      <div className="text-gray-700 dark:text-gray-300" data-testid={`fleet-${id}-first-saved`}>
        Τελευταία ώρα αποθήκευσε πρώτος <b>{firstSaved ?? 0}</b> τραγούδια <span className="text-gray-400">(βάση)</span>
      </div>
      {problems.length === 0 && <div className="text-green-600 dark:text-green-400" data-testid={`fleet-${id}-no-problems`}>Καμία βλάβη που να κρατά πάνω από 15΄</div>}
      {problems.map((p) => (
        <div key={p.code} className="text-red-600 dark:text-red-400" data-testid={`fleet-${id}-problem-${p.code}`}>
          {(PROBLEMS[p.code] || (() => p.code))(p)} · από {fmtTime(p.since)}
        </div>
      ))}
    </div>
  );
};

export const FleetServersPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [common, setCommon] = useState(null);
  const [today, setToday] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/diagnostic/fleet-servers`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = await res.json();
        if (!cancelled) { setData(j); setError(null); }
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [token]);

  const exportJson = (e) => {
    e.stopPropagation();
    const blob = new Blob([JSON.stringify({ ...data, today, common_songs_last_60m: common }, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `servers-report-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const byFirst = data?.contribution_last_60m?.by_first_writer || {};
  const stopped = data?.stopped_servers || [];
  return (
    <div data-testid="fleet-servers-panel">
      <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
        <Server className="w-3 h-3" /> Servers
        {data && (
          <span className="ml-auto flex items-center gap-3">
            <JingleReportButton token={token} />
            <button type="button" onClick={exportJson} className="flex items-center gap-1 normal-case font-normal text-blue-600 dark:text-blue-400 hover:underline" data-testid="fleet-export-json-btn">
              <FileJson className="w-3 h-3" /> JSON
            </button>
          </span>
        )}
      </div>
      {error && <div className="text-xs text-red-600" data-testid="fleet-servers-error">Σφάλμα: {error}</div>}
      <TodaySection token={token} onLoaded={setToday} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {(data?.servers || []).filter((s) => !s.stale).map((s) => (
          <ServerCard key={s.server} s={s} firstSaved={byFirst[s.server]} />
        ))}
      </div>
      <CommonSongsSection token={token} onLoaded={setCommon} />
      {stopped.length > 0 && (
        <div className="text-[11px] text-gray-500 mt-1.5" data-testid="fleet-stopped-servers">
          Επανεκκινήσεις / servers που σταμάτησαν: {stopped.map((x) => `${x.server} (${fmtTime(x.boot_at)}–${fmtTime(x.last_seen)})`).join(' · ')}
        </div>
      )}
    </div>
  );
};
